import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { setGallerySizes } from '@theme/media-gallery';
import { nostoLogger, reloadNostoPlacements } from '@theme/nosto-placements';

const debug = nostoLogger('cart-confirmation-cross-sell');

/**
 * Builds the recommendations request URL, preserving Shopify preview
 * parameters from the current page URL (same helper as cart-cross-sell.js).
 * @returns {URL}
 */
function buildRecommendationsURL() {
  const currentUrl = new URL(window.location.href);
  const recommendationsUrl = new URL(Theme.routes.product_recommendations_url, currentUrl);

  for (const [key, value] of currentUrl.searchParams.entries()) {
    if (!recommendationsUrl.searchParams.has(key)) {
      recommendationsUrl.searchParams.set(key, value);
    }
  }

  return recommendationsUrl;
}

/**
 * "Vous pourriez aussi aimer" grid inside the cart confirmation layer, refreshed for the just-added
 * product on every add. Two sources, decided server-side by the `cart_cross_sell_nosto_placement_id`
 * theme setting (cf. the snippet's doc block):
 *
 * - **Nosto** (`data-nosto-placement-id` present): the container ships empty and Nosto's Session API
 *   is asked to fill it, exactly like the PDP placements after a color swap. It has to be reloaded on
 *   every opening, since the reference product changes each time while Nosto's onsite script only
 *   reads product context at page load.
 * - **Shopify recommendations** (fallback, while no cart recommendation exists in the Nosto
 *   dashboard): isolated section rendering, same mechanism as cart-cross-sell.js.
 *
 * Ordering note for the Nosto branch: the layer's own section is morphed synchronously during the
 * CartAddEvent dispatch, which resets the container to empty. The reload is a network round-trip and
 * Nosto injects by container id, so it always lands on the post-morph container whichever listener
 * runs first.
 */
class CartConfirmationCrossSell extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {MutationObserver | null} */
  #injectionObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#onCartUpdate);
    this.#watchInjectedCards();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#onCartUpdate);
    this.#abortController?.abort();
    this.#injectionObserver?.disconnect();
  }

  /**
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    if (!(event instanceof CartAddEvent) && !(event instanceof CartUpdateEvent)) return;

    const productId = event.detail?.data?.productId;
    if (!productId) return;

    const nostoPlacementId = this.dataset.nostoPlacementId;
    if (nostoPlacementId) {
      reloadNostoPlacements(String(productId), [nostoPlacementId], debug);
      return;
    }

    this.#load(String(productId));
  };

  /**
   * Nosto branch only: Dynamic Cards come from the standalone `product-card` section, which carries
   * its own `sizes` — far too wide for two cards in a 393px panel. Rewritten as soon as a card shows
   * up, the injection being asynchronous and out of our hands (same treatment as the collection grid,
   * cf. assets/nosto-collection.js).
   */
  #watchInjectedCards() {
    const sizes = this.dataset.cardSizes;
    if (!sizes || !this.dataset.nostoPlacementId) return;

    this.#injectionObserver = new MutationObserver(() => {
      if (!this.querySelector('product-card')) return;
      setGallerySizes(this, sizes);
    });

    this.#injectionObserver.observe(this, { childList: true, subtree: true });
  }

  /**
   * @param {string} productId
   */
  async #load(productId) {
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    const url = buildRecommendationsURL();
    url.searchParams.set('section_id', this.dataset.sectionId || '');
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', this.dataset.productLimit || '6');
    const intent = this.dataset.intent || 'related';
    url.searchParams.set('intent', intent);

    try {
      let source = await this.#fetchSource(url.toString(), abortController.signal);

      // Shopify's complementary intent only returns products explicitly
      // configured in Search & Discovery, with no automatic fallback — retry
      // with related (same behaviour as cart-cross-sell.js #buildFallbackURL).
      if ((!source || source.hidden) && intent !== 'related') {
        url.searchParams.set('intent', 'related');
        source = await this.#fetchSource(url.toString(), abortController.signal);
      }

      if (!(source instanceof HTMLElement)) {
        this.hidden = true;
        this.replaceChildren();
        return;
      }

      this.className = source.className;
      this.hidden = source.hidden;
      this.replaceChildren(...source.childNodes);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
    }
  }

  /**
   * @param {string} url
   * @param {AbortSignal} signal
   * @returns {Promise<HTMLElement | null>}
   */
  async #fetchSource(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) return null;

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const source = html.querySelector('cart-confirmation-cross-sell-component');

    return source instanceof HTMLElement ? source : null;
  }
}

if (!customElements.get('cart-confirmation-cross-sell-component')) {
  customElements.define('cart-confirmation-cross-sell-component', CartConfirmationCrossSell);
}
